import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, NewPasswordChallenge } from "@/contexts/AuthContext";
import { Button, Input, Label } from "@lumanity/ui/primitives";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New password challenge state
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const { login, completeNewPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const result = await login(email, password);
      if (result && "challenge" in result) {
        setChallenge(result.challenge);
      } else {
        navigate("/", { replace: true });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed. Please check your credentials.";
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!challenge) return;
    setError("");
    setIsSubmitting(true);
    try {
      await completeNewPassword(challenge.cognitoUser, newPassword);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to set new password.";
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const wrapper = (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src="/logo-icon.svg" alt="Logo" className="w-10 h-10" />
          <span className="text-2xl font-bold text-foreground">Lumanity</span>
        </div>
        <div className="bg-card border rounded-lg p-6">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm rounded-md p-3 mb-4">
              {error}
            </div>
          )}
          {challenge ? (
            <>
              <h1 className="text-xl font-semibold text-foreground mb-1">Set your password</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Your account requires a new password before you can continue.
              </p>
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1.5"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1.5"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-brand-teal text-white hover:bg-brand-teal/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Setting password..." : "Set Password"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-foreground mb-1">Sign in</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Enter your credentials to access your account
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-brand-teal text-white hover:bg-brand-teal/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return wrapper;
};

export default Login;
